import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";

import { TCatalogEntities } from "types";
import Pipeline from "../Pipeline";

import { sortOptionsByFavourites } from "../helper";
import List from "../../List";
import Filter from "../../Filter";

import MarkeReducer from "./MarkeReducer";
import MarkeIndicator from "./MarkeIndicator";

interface IFilter {
  entities: TCatalogEntities;
  value: number[];
  dispatchFilter: any;
  configuration?: any;
}

export const SideFilter: React.FC<IFilter> = ({
  entities = {},
  configuration = {},
  value = [],
  dispatchFilter = () => null,
}): JSX.Element | null => {
  const { formatMessage: t } = useIntl();

  if (!entities.marke) return null;

  const items = Object.values(entities.marke);
  const itemsCount = items.length;

  const setValueHandler = (value: any) => {
    // Check if all variants are selected
    const newValue = value.length === itemsCount ? [] : value;
    dispatchFilter({ type: "UPDATE_FILTER", payload: { marke: newValue } });
  };

  const favourites = configuration?.filter?.favourites?.marke || [];
  const options = items
    .map(({ id, name }) => ({
      value: id,
      title: name,
    }))
    .sort(sortOptionsByFavourites(favourites));

  const all = {
    title: t({ id: "common.alle", defaultMessage: "Alle" }),
  };

  const Component = (
    <Box sx={{ width: 1 }}>
      <List {...{ value, options, setValueHandler, all, sort: false }} />
    </Box>
  );
  return (
    <Filter
      component={Component}
      title={
        <FormattedMessage id="filter.marke.label" defaultMessage="Standort" />
      }
    />
  );
};

export const SimpleFilter: React.FC<IFilter> = ({
  entities = {},
  configuration = {},
  value = [],
  dispatchFilter = () => null,
}): JSX.Element | null => {
  const { formatMessage: t } = useIntl();
  if (!entities.marke) return null;

  const setValueHandler = (event: SelectChangeEvent) => {
    const value = parseInt(event.target.value);
    const newValue = value === 0 ? [] : [value];
    dispatchFilter({
      type: "UPDATE_FILTER",
      payload: { marke: newValue, modell: [] },
    });
  };

  const favourites = configuration?.filter?.favourites?.marke || [];
  const items = Object.values(entities.marke);
  const options = items
    .map(({ id, name }) => ({
      value: id,
      title: name,
    }))
    .sort(sortOptionsByFavourites(favourites));

  return (
    <FormControl size="small">
      <InputLabel id="filter-marke-simple-label">
        <FormattedMessage id="filter.marke.label" defaultMessage="Hersteller" />
      </InputLabel>
      <Select
        labelId="filter-marke-simple-label"
        id="filter-marke-simple"
        value={value.length ? value[0].toString() : "0"}
        label={t({ id: "filter.marke.label", defaultMessage: "Hersteller" })}
        onChange={setValueHandler}
        autoWidth={false}
      >
        <MenuItem value={0}>
          {t({ id: "common.alle", defaultMessage: "Alle" })}
        </MenuItem>
        {options.map(({ value, title }) => (
          <MenuItem key={"marke-" + value} value={value}>
            {title}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const variants = new Map([
  ["side", SideFilter],
  ["simple", SimpleFilter],
]);

interface IContainer {
  Pipe?: any;
  variant?: string;
}

const Container: React.FC<IContainer> = ({
  Pipe = Pipeline,
  variant = "side",
}) => {
  const Filter = variants.get(variant) || SimpleFilter;
  return (
    <Pipe
      {...{
        type: "marke",
        defaultValue: [],
        Component: Filter,
        Reducer: MarkeReducer,
        Indicator: MarkeIndicator,
      }}
    />
  );
};

export default Container;
